import React, { useEffect, useRef } from "react";
import Chart from "chart.js/auto";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { getCookie } from "../../../services/cookie";
import { useLocation } from "react-router-dom";

const scope1And2Topics = [
  "stationary_combustion",
  "mobile_combustion",
  "refrigerants",
  "purchased_electricity",
];

const scope3Topics = [
  "purchased_goods_and_services",
  "fuel_and_energy_related_activities",
  "waste",
  "employee_commute",
  "downstream_t_and_d",
  "use_of_sold_products",
  "franchises",
  "end_of_life_treatment_of_sold_products",
  "capital_goods",
  "upstream_t_and_d",
  "business_travel",
  "upstream_leased_assets",
  "processing_of_sold_products",
  "downstream_leased_assets",
  "investments",
  "upstream_leased_asset",
  "downstream_leased_asset",
  "end_of_life_treatment",
];

const colorPalette = [
  "#846BB5",
  "#FBCE74",
  "#6FBFBA",
  "#C5B9DB",
  "#F1B2C4",
  "#D291BC",
  "#FFDAB9",
  "#9FD3C7",
  "#FFB6B9",
  "#BFD7EA",
  "#FFDDC1",
  "#D5AAFF",
  "#B2F7EF",
  "#B5EAD7",
  "#C7CEEA",
  "#F3B0C3",
  "#B4F8C8",
  "#FFDAC1",
  "#AFCBFF",
  "#C1C8E4",
];

const createChart = (
  ctx,
  chartData,
  labels,
  colors,
  legendId,
  fontSize,
  gapSize
) => {
  return new Chart(ctx, {
    type: "pie",
    data: {
      labels,
      datasets: [
        {
          data: chartData,
          backgroundColor: colors,
        },
      ],
    },
    options: {
      plugins: {
        legend: {
          display: false,
        },
        datalabels: {
          color: "white",
          font: { weight: "bold", size: 12 },
          formatter: (value, context) => {
            const total = context.dataset.data.reduce((a, b) => a + b, 0);
            const percent = ((value / total) * 100).toFixed(0) + "%";
            return percent === "0%" ? "" : percent;
          },
        },
      },
      maintainAspectRatio: false,
      responsive: true,
    },
    plugins: [
      ChartDataLabels,
      {
        id: "customLegendPlugin",
        afterUpdate(chart) {
          const ul = document.getElementById(legendId);
          if (!ul) return;
          ul.innerHTML = "";

          const labels = chart.data.labels;
          const colors = chart.data.datasets[0].backgroundColor;

          if (legendId === "scope3-legend") {
            const col1 = document.createElement("ul");
            const col2 = document.createElement("ul");
            col1.style.listStyle = "none";
            col2.style.listStyle = "none";
            col1.style.paddingLeft = "0px";
            col2.style.paddingLeft = "0px";

            labels.forEach((label, i) => {
              const item = document.createElement("li");
              item.style.display = "flex";
              item.style.alignItems = "center";
              item.style.fontSize = fontSize;
              item.style.marginBottom = "2px";

              const dotSize = fontSize === "9px" ? "8px" : "12px"; // shrink dot too
              item.innerHTML = `
  <span style="width: ${dotSize}; height: ${dotSize}; border-radius: 50%; background-color: ${colors[i]}; display: inline-block; margin-right: 6px;"></span>
  ${label}
`;

              item.innerHTML = `
                <span style="width: 12px; height: 12px; border-radius: 50%; background-color: ${colors[i]}; display: inline-block; margin-right: 8px;"></span>
                ${label}
              `;
              (i < 10 ? col1 : col2).appendChild(item);
            });

            ul.style.display = "flex";
            ul.style.gap = gapSize; // use the dynamic value passed
            ul.style.maxHeight = "260px"; // ✅ adjust this as needed
            ul.style.overflowY = "auto"; // ✅ allow scroll if overflow
            ul.style.alignItems = "flex-start"; // ✅ fix vertical alignment
            ul.appendChild(col1);
            ul.appendChild(col2);
          } else {
            labels.forEach((label, i) => {
              const item = document.createElement("li");
              item.style.display = "flex";
              item.style.alignItems = "flex-start";
              item.style.fontSize = fontSize;
              item.style.marginBottom = fontSize === "7px" ? "1px" : "4px";
              item.style.wordBreak = "break-word";
              item.style.maxWidth = fontSize === "7px" ? "80px" : "unset";

              const dotSize = fontSize === "7px" ? "6px" : "12px";
              item.innerHTML = `
                <span style="width: 12px; height: 12px; border-radius: 50%; background-color: ${colors[i]}; display: inline-block; margin-right: 6px; margin-top: 2px;"></span>
                ${label}
              `;
              ul.appendChild(item);
            });
          }
        },
      },
    ],
  });
};

const EmissionTypeChartScopeBasis = ({ data }) => {
  const role = getCookie("role");
  const scope1And2Ref = useRef(null);
  const scope3Ref = useRef(null);
  const chartInstance1 = useRef(null);
  const chartInstance2 = useRef(null);
  const location = useLocation();
  const isConfigureDashboard = location.pathname.includes("dashboard-config"); // adjusts to your route

  const gapBetweenLegendColumns = isConfigureDashboard ? "16px" : "24px";
  const [containerWidth, setContainerWidth] = React.useState(0);
  const containerRef = useRef(null);

  const chartSize =
    containerWidth >= 1000 ? 280 : containerWidth >= 600 ? 200 : 160; // smaller fallback

  const legendFontSize =
    containerWidth >= 1000 ? "13px" : containerWidth >= 600 ? "11px" : "9px";

  useEffect(() => {
    const observer = new ResizeObserver((entries) => {
      for (let entry of entries) {
        requestAnimationFrame(() => {
          setContainerWidth(entry.contentRect.width);
        });
      }
    });

    if (containerRef.current) observer.observe(containerRef.current);

    return () => {
      if (containerRef.current) observer.unobserve(containerRef.current);
    };
  }, []);

  const getChartValues = (filteredItems) => {
    return role === "sustainability_manager" ||
      role === "read_only_user" ||
      role === "proxy_sustainability_manager"
      ? filteredItems.map((entry) => entry.total_co2e)
      : filteredItems.map((entry) => entry.total);
  };

  useEffect(() => {
    if (containerWidth < 100) return; // ✅ wait until container width is valid

    const filteredScope1And2 = data.filter((d) =>
      scope1And2Topics.includes(d.type)
    );
    const filteredScope3 = data.filter((d) => scope3Topics.includes(d.type));

    if (chartInstance1.current) chartInstance1.current.destroy();
    if (chartInstance2.current) chartInstance2.current.destroy();

    chartInstance1.current = createChart(
      scope1And2Ref.current.getContext("2d"),
      getChartValues(filteredScope1And2),
      filteredScope1And2.map((d) =>
        d.type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
      ),
      colorPalette.slice(0, filteredScope1And2.length),
      "scope1-legend",
      legendFontSize,
      gapBetweenLegendColumns
    );

    chartInstance2.current = createChart(
      scope3Ref.current.getContext("2d"),
      getChartValues(filteredScope3),
      filteredScope3.map((d) =>
        d.type.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase())
      ),
      colorPalette.slice(0, filteredScope3.length),
      "scope3-legend",
      legendFontSize,
      gapBetweenLegendColumns
    );
  }, [data, containerWidth]); // ✅ include containerWidth here

  const legendStyle = {
    listStyle: "none",
    paddingLeft: 0,
    marginTop: 12,
  };

  return (
    <div
      ref={containerRef}
      style={{
        display: "flex",
        gap: "20px",
        width: "100%",
        justifyContent: "flex-start",
      }}
    >
      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <h4 style={{ margin: 0, fontWeight: "400", fontSize: "15px" }}>
          Scope 1 and 2
        </h4>
        <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
          <div style={{ width: `${chartSize}px`, height: `${chartSize}px` }}>
            <canvas ref={scope1And2Ref} />
          </div>
          <ul id="scope1-legend" style={legendStyle}></ul>
        </div>
      </div>

      <div
        style={{
          width: "1px",
          backgroundColor: "#ccc",
          height: "318px",
          margin: "0 16px",
        }}
      ></div>

      <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
        <h4 style={{ margin: 0, fontWeight: "400", fontSize: "15px" }}>
          Scope 3
        </h4>
        <div style={{ display: "flex", gap: "12px", alignItems: "flex-start" }}>
          <div style={{ width: `${chartSize}px`, height: `${chartSize}px` }}>
            <canvas ref={scope3Ref} />
          </div>
          <ul id="scope3-legend" style={legendStyle}></ul>
        </div>
      </div>
    </div>
  );
};

export default EmissionTypeChartScopeBasis;
